/**
 * The Performance Insights contract — one flat router, `performanceInsightsContract`
 * (`/performance/insights`). See
 * `docs/plans/performance-management/performance-insights-plan.md`'s "Contract & API" section for
 * the full design this file implements. `tsRestRouter` (`apps/api/src/lib/ts-rest.ts`) throws on a
 * nested sub-router, so — matching `goal.contract.ts`/`review.contract.ts`'s precedent — this stays
 * one flat router rather than nine grouped under sub-paths.
 *
 * THIS SECTION IS A PURE READ LAYER over Section 2 (`Goal`/`GoalCheckIn`) and Section 4
 * (`PerformanceReview`/`ReviewCompetencyRating`) — no new writable resource of its own, `getScoreTrend`
 * is also what Section 1's Dashboard trajectory chart calls directly (one IA, two surfaces — do not
 * fork a second version of it), and there is no separate admin contract: `apps/web`'s admin
 * `InsightsOverview` calls these same nine endpoints with a wider `scope`/`scopeId`.
 *
 * ROUTE-LAYER RULES THIS CONTRACT ASSUMES (enforced in `apps/api/src/routes/performance-insights.ts`,
 * not here — contract only, see the plan's T2 vs T4/T5/T6 split):
 *   - `scope: 'org'` is served ONLY from the `ReviewCycleRollup` table for a closed cycle, never live
 *     SQL — that is what keeps the expensive aggregation path unreachable by a single request
 *     regardless of tenant size. Live `$queryRaw`/`groupBy` only runs for an OPEN cycle.
 *   - k-anonymity floor `n < 5`: every cohort aggregate below the floor is suppressed with
 *     `{suppressed: true, reason: 'cohort_too_small'}` and NO counts anywhere in the body — see
 *     `suppressibleCohort()` below, used by `getRatingDistribution` and per-row by `getSkillGaps`.
 *     `getTalentGrid` is the one deliberate carve-out the plan specifies: a cell's `count` is always
 *     shown, only its `people` list is omitted below the floor (identity, not headcount, is the
 *     sensitive part of a 9-box cell).
 *   - Scoped reads that fail (another manager's team, another org's scopeId, ...) return 404, never
 *     403 — 403 is reserved for "does not hold `analytics.performance:read`/`:export`" at all.
 *   - `analytics.performance:read` is MANAGER (capped to `reporting_tree` scope by `ROLE_MAX_SCOPE`),
 *     HR_MANAGER, HR_ADMIN, ORG_OWNER, AUDITOR. `analytics.performance:export` is the same list minus
 *     AUDITOR (read-only, never export) — see `packages/shared/src/permissions/roles.ts`.
 *
 * Decimal-bearing numeric fields — `mean`, `stdDev`, `score`, `benchmarkScore`, `overallScore`,
 * `avgProgressPercent`/`goalsMetPercent` (percentages), `avgRatedLevel`/`gap` — are decimal STRINGS
 * on the wire per CLAUDE.md invariant 2, never `z.number()`. Plain integer counts (`total`, `count`,
 * `submitted`, `daysOverdue`, ...) stay `z.number().int()` — they are never averaged/weighted, so a
 * float representation is not the invariant-2 hazard the money/score fields are. `normalCurve`'s
 * `{x, y}` points are sampled chart coordinates, not stored/averaged business data, and are also kept
 * as decimal strings for consistency with every other axis value a `LineChart`/`BarChart` renders.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  INSIGHTS_PERSON_STATUSES,
  REVIEW_CYCLE_ROLLUP_SCOPES,
  REVIEW_CYCLE_TYPES,
  TALENT_GRID_BANDS,
} from '../enums.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const insightsScopeSchema = z.enum(REVIEW_CYCLE_ROLLUP_SCOPES);
export const insightsPersonStatusSchema = z.enum(INSIGHTS_PERSON_STATUSES);
export const talentGridBandSchema = z.enum(TALENT_GRID_BANDS);
/** Mirrors `reviewCycleTypeSchema` (`review-cycle.contract.ts`) — duplicated as a plain
 *  `z.enum(REVIEW_CYCLE_TYPES)` rather than imported, since this file otherwise has no dependency
 *  on `review-cycle.contract.ts` and importing one enum schema from it would pull a cross-contract
 *  coupling in for no shared behaviour (both are just `z.enum` over the same source-of-truth
 *  `REVIEW_CYCLE_TYPES` array in `enums.ts`). */
export const scoreTrendCycleTypeSchema = z.enum(REVIEW_CYCLE_TYPES);

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

/**
 * A comma-separated list of uuids in a query string (`?cycleIds=a,b,c`) — ts-rest/Express never
 * parses a repeated `?cycleIds=a&cycleIds=b` into an array without extra middleware, so this is the
 * one query-array convention this contract uses. Also accepts a real array for a client that already
 * has one (e.g. a generated fetch built from a typed array), so the route never has to care which.
 */
const uuidCsvQuerySchema = z
  .union([z.array(uuidSchema), z.string()])
  .transform((value) => (Array.isArray(value) ? value : value.split(',').map((id) => id.trim())))
  .pipe(z.array(uuidSchema));

/**
 * The k-anonymity floor's shape, reused everywhere a cohort aggregate can be suppressed: `n < 5`
 * means `{suppressed: true, reason: 'cohort_too_small'}` (plus whatever non-count identifying
 * fields — a competency id, a team id — are safe to keep) and the discriminated union makes it
 * impossible for the "real" (count-bearing) fields to be present alongside `suppressed: true` — no
 * matter how the route composes the response, a suppressed body cannot carry a count. `common` is
 * present on both branches (e.g. which competency/team this row is about); `shape` is the
 * count-bearing data that only exists on the non-suppressed branch. See this file's header.
 */
function suppressibleCohort<Common extends z.ZodRawShape, Shape extends z.ZodRawShape>(
  common: Common,
  shape: Shape,
) {
  return z.discriminatedUnion('suppressed', [
    z.object({
      ...common,
      suppressed: z.literal(true),
      reason: z.literal('cohort_too_small'),
    }),
    z.object({
      ...common,
      suppressed: z.literal(false),
      ...shape,
    }),
  ]);
}

// ---------------------------------------------------------------------------
// DTOs — getScoreTrend
// ---------------------------------------------------------------------------

/**
 * One point on the trajectory chart. `score` is nullable — an open cycle a person hasn't been rated
 * in yet has no `overallScore` — while `benchmarkScore`/`benchmarkSuppressed` carry the same
 * k-anonymity floor as every other cohort read here: below 5 peers, `benchmarkScore` is `null` and
 * `benchmarkSuppressed` is `true` (never a real peer average computed over fewer than 5 people).
 * This is the exact shape Section 1's Dashboard trajectory chart consumes — see this file's header.
 */
export const scoreTrendPointSchema = z.object({
  cycleId: uuidSchema,
  cycleLabel: z.string(),
  cycleStartsOn: dateOnlySchema,
  /** The cycle's real cadence (annual/half_yearly/quarterly/probation/project) — lets a caller
   *  filter the trend by cadence (e.g. "show only quarterly cycles") without a second request.
   *  Zero extra query cost: the handler already loads the full `ReviewCycle` row for every point. */
  cycleType: scoreTrendCycleTypeSchema,
  score: decimalStringSchema.nullable(),
  benchmarkScore: decimalStringSchema.nullable(),
  benchmarkSuppressed: z.boolean(),
});
export type ScoreTrendPoint = z.infer<typeof scoreTrendPointSchema>;

export const getScoreTrendQuerySchema = z.object({
  /** Defaults to the caller's own trend when omitted. */
  personId: uuidSchema.optional(),
  /** A department-level average trend instead of one person's — mutually meaningful with
   * `personId` omitted; the route decides precedence if both are somehow sent. */
  departmentId: uuidSchema.optional(),
  /** Restricts to specific cycles (closed or open); omitted means "every cycle the caller may see",
   * newest-last. */
  cycleIds: uuidCsvQuerySchema.optional(),
});

// ---------------------------------------------------------------------------
// DTOs — getRatingDistribution
// ---------------------------------------------------------------------------

/** One legend/bell-curve bucket, e.g. "Exceeds Expectations" — the label matches the cycle's own
 * `ReviewCycle.ratingBands[].band`, not a fixed enum, since bands are HR-configurable per cycle. */
export const ratingDistributionBucketSchema = z.object({
  band: z.string(),
  count: z.number().int().nonnegative(),
  percent: percentSchema,
});
export type RatingDistributionBucket = z.infer<typeof ratingDistributionBucketSchema>;

/** One sampled point of the server-computed normal-curve overlay — sampled server-side (not
 * per-client) so web, admin, and mobile draw the identical curve without reimplementing the stats. */
export const normalCurvePointSchema = z.object({
  x: decimalStringSchema,
  y: decimalStringSchema,
});
export type NormalCurvePoint = z.infer<typeof normalCurvePointSchema>;

export const ratingDistributionSchema = suppressibleCohort(
  {},
  {
    buckets: z.array(ratingDistributionBucketSchema),
    total: z.number().int().nonnegative(),
    mean: decimalStringSchema,
    stdDev: decimalStringSchema,
    normalCurve: z.array(normalCurvePointSchema),
  },
);
export type RatingDistribution = z.infer<typeof ratingDistributionSchema>;

export const getRatingDistributionQuerySchema = z.object({
  scope: insightsScopeSchema,
  /** Required for `team`/`department`/`legal_entity`; omitted for `org` (the scope is the tenant
   * itself) and for a manager's own `team` (resolved from the caller) — the route enforces which. */
  scopeId: uuidSchema.optional(),
  cycleId: uuidSchema,
});

// ---------------------------------------------------------------------------
// DTOs — getGoalAnalytics
// ---------------------------------------------------------------------------

const goalAnalyticsCountsSchema = z.object({
  onTrackCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  atRiskCount: z.number().int().nonnegative(),
  overdueCount: z.number().int().nonnegative(),
  avgProgressPercent: percentSchema,
});

export const goalAnalyticsDepartmentRowSchema = goalAnalyticsCountsSchema.extend({
  departmentId: uuidSchema,
  departmentName: z.string(),
});
export type GoalAnalyticsDepartmentRow = z.infer<typeof goalAnalyticsDepartmentRowSchema>;

export const goalAnalyticsSchema = goalAnalyticsCountsSchema.extend({
  /** Present only when `byDepartment: true` was requested — the plan's "optionally by department". */
  byDepartment: z.array(goalAnalyticsDepartmentRowSchema).optional(),
});
export type GoalAnalytics = z.infer<typeof goalAnalyticsSchema>;

export const getGoalAnalyticsQuerySchema = z.object({
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
  /** Adds `byDepartment` to the response — skipped by default since a manager's own `team` scope
   * has nothing meaningful to break down further. */
  byDepartment: z.coerce.boolean().optional(),
});

// ---------------------------------------------------------------------------
// DTOs — getTeamBreakdown / getAttentionList (shared row shape)
// ---------------------------------------------------------------------------

/** One report row. Not k-anonymity-suppressed — this is always the caller's own resolved,
 * permission-gated report set (see this file's header on the once-resolve-then-gate rule), never a
 * cross-tenant cohort statistic, so the floor that guards `getRatingDistribution`/`getSkillGaps`
 * does not apply here. */
export const teamBreakdownRowSchema = z.object({
  personId: uuidSchema,
  displayName: z.string(),
  designation: z.string().nullable(),
  /** Null when the person has no submitted review yet in the scope's active/queried cycle. */
  overallScore: decimalStringSchema.nullable(),
  /** Resolved server-side from `ReviewCycle.targetScoreByGrade` for the person's current grade;
   * null when the person has no grade recorded or no target is configured for that grade — never
   * fabricated or defaulted to a guess. */
  targetScore: decimalStringSchema.nullable(),
  status: insightsPersonStatusSchema,
  goalsMetPercent: percentSchema,
  daysOverdue: z.number().int().nonnegative(),
  /** The row's `PerformanceReview.id` for the resolved cycle — null exactly when `overallScore` is
   *  null (no review row exists yet for this person in this cycle). Lets a caller with `review:manage`
   *  act on the underlying review (e.g. `reviewContract.nudgeReview`) without a second lookup; a
   *  caller without that permission simply has no route that accepts it. */
  reviewId: uuidSchema.nullable(),
});
export type TeamBreakdownRow = z.infer<typeof teamBreakdownRowSchema>;

export const getTeamBreakdownQuerySchema = z.object({
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
  /** Omitted falls back to the scope's current active cycle — the route resolves it. */
  cycleId: uuidSchema.optional(),
});

/** `getAttentionList`'s row — a `teamBreakdownRowSchema` plus the mockup's "Schedule 1:1 Focus
 * Sync" affordance. `suggestedAction` is a single literal today (no other action has been designed),
 * kept as a literal rather than a freeform string so a client's deep-link switch stays exhaustive. */
export const attentionRowSchema = teamBreakdownRowSchema.extend({
  suggestedAction: z.literal('schedule_1on1'),
  /** 1 = baseline (on the attention list, no automated escalation fired yet — the existing manual
   *  "Send Ping" is this tier and is never represented as a row here); 2 = the HR_MANAGER
   *  notification has fired (>= 3 days overdue); 3 = the department-head notification has also
   *  fired (>= 7 days overdue). Resolved server-side from PerformanceAttentionEscalation's
   *  timestamps in a later step — never null, since every row on this list is at least tier 1 by
   *  definition of being on the list. */
  escalationLevel: z.number().int().min(1).max(3),
});
export type AttentionRow = z.infer<typeof attentionRowSchema>;

export const getAttentionListQuerySchema = z.object({
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
  cycleId: uuidSchema.optional(),
  /** A score floor below which a report is flagged — decimal string, per invariant 2. Omitted uses
   * the route's own default threshold. */
  threshold: decimalStringSchema.optional(),
});

// ---------------------------------------------------------------------------
// DTOs — getCycleCompletion
// ---------------------------------------------------------------------------

export const cycleCompletionSchema = z.object({
  submitted: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: percentSchema,
});
export type CycleCompletion = z.infer<typeof cycleCompletionSchema>;

export const getCycleCompletionQuerySchema = z.object({
  cycleId: uuidSchema,
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// DTOs — getTalentGrid
// ---------------------------------------------------------------------------

/** One cell of the classic 3x3 (`performanceBand` x `potentialBand`) 9-box grid. `count` is always
 * present — the plan's explicit carve-out from the general k-anonymity floor (identity, not
 * headcount, is what a small cell risks exposing); `people` is omitted entirely below the floor. */
export const talentGridCellSchema = z.object({
  performanceBand: talentGridBandSchema,
  potentialBand: talentGridBandSchema,
  count: z.number().int().nonnegative(),
  /** Omitted (not empty-array) when `count < 5` — see this schema's header. */
  people: z
    .array(
      z.object({
        personId: uuidSchema,
        displayName: z.string(),
      }),
    )
    .optional(),
});
export type TalentGridCell = z.infer<typeof talentGridCellSchema>;

export const getTalentGridQuerySchema = z.object({
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
  /** Omitted falls back to the scope's current active cycle. */
  cycleId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// DTOs — getSkillGaps
// ---------------------------------------------------------------------------

/**
 * One competency's rated-vs-expected gap for one team. Full k-anonymity floor applies per row (this
 * IS a cross-person cohort average, unlike `teamBreakdownRowSchema`) — `sampleSize` is exactly the
 * kind of count invariant 2's suppression rule forbids in a suppressed body, so it lives inside the
 * `suppressibleCohort()` union alongside the averages it would otherwise expose the size of.
 */
export const skillGapRowSchema = suppressibleCohort(
  {
    competencyId: uuidSchema,
    competencyName: z.string(),
    /** Null at `scope: 'org'` — the row is aggregated across the whole org, not one team. */
    teamId: uuidSchema.nullable(),
    teamName: z.string().nullable(),
  },
  {
    /** Mean of `ReviewCompetencyRating.finalRating` (falling back to `managerRating`) across the
     * team, on the cycle's rating scale. */
    avgRatedLevel: decimalStringSchema,
    /** `Competency.expectedLevelByGrade`'s value for the grade(s) present in the team — an
     * integer level on the same scale, per that field's own definition. */
    expectedLevel: z.number().int(),
    /** `avgRatedLevel - expectedLevel`, signed — negative means the team is rated below the
     * expected level for that competency. */
    gap: decimalStringSchema,
    sampleSize: z.number().int().nonnegative(),
  },
);
export type SkillGapRow = z.infer<typeof skillGapRowSchema>;

export const getSkillGapsQuerySchema = z.object({
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
  cycleId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// DTOs — exportSummary
// ---------------------------------------------------------------------------

/**
 * Org-scale export only — a single person's PDF is `reviewContract.exportReviewPdf` (in-request,
 * Section 4 owns it). `format` is CSV or Excel: decision Q10 makes `excel` a real `.xlsx` (the
 * worker's job), `csv` stays a separate option. Org-scale PDF is explicitly deferred to its own
 * library decision and is not part of this endpoint's contract.
 */
export const exportSummaryBodySchema = z.object({
  format: z.enum(['csv', 'excel']).default('csv'),
  scope: insightsScopeSchema,
  scopeId: uuidSchema.optional(),
  cycleId: uuidSchema.optional(),
});

/**
 * ASYNC ONLY (performance remediation #23 / N18). The response is the job id and nothing else — no
 * inline file, no data URL, no names or scores. The idempotency middleware stores this body for 24h
 * so a retried request returns the same job; with only an id in it, nothing sensitive is persisted.
 * The requester is notified when the file is ready and downloads it through `getExportDownload`.
 */
export const exportSummaryResponseSchema = z.object({
  jobId: uuidSchema,
});
export type ExportSummaryResponse = z.infer<typeof exportSummaryResponseSchema>;

export const exportDownloadParamsSchema = z.object({ jobId: uuidSchema });

/** A fresh presigned URL, minted on demand for the requester only — never carried in a notification. */
export const exportDownloadResponseSchema = z.object({
  downloadUrl: z.string(),
  expiresAt: z.string().datetime(),
});
export type ExportDownloadResponse = z.infer<typeof exportDownloadResponseSchema>;

// ---------------------------------------------------------------------------
// Contract — /performance/insights
// ---------------------------------------------------------------------------

export const performanceInsightsContract = c.router(
  {
    getScoreTrend: {
      method: 'GET',
      path: '/score-trend',
      query: getScoreTrendQuerySchema,
      responses: {
        200: z.array(scoreTrendPointSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Score-over-cycles trajectory for a person or department, with a k-anonymity-floored peer benchmark — also called directly by Section 1's Dashboard trajectory chart",
      metadata: routeMeta(null),
    },

    getRatingDistribution: {
      method: 'GET',
      path: '/rating-distribution',
      query: getRatingDistributionQuerySchema,
      responses: {
        200: ratingDistributionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Bell-curve rating distribution for one cycle at one scope — server-sampled normal-curve overlay, suppressed below n=5 with no counts in the body',
      metadata: routeMeta(null),
    },

    getGoalAnalytics: {
      method: 'GET',
      path: '/goal-analytics',
      query: getGoalAnalyticsQuerySchema,
      responses: {
        200: goalAnalyticsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'On-track/completed/at-risk goal counts, average progress, and overdue count, optionally broken down by department',
      metadata: routeMeta(null),
    },

    getTeamBreakdown: {
      method: 'GET',
      path: '/team-breakdown',
      query: getTeamBreakdownQuerySchema,
      responses: {
        200: z.array(teamBreakdownRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'One row per report in the resolved scope — "Team Member Breakdown" on the mockup; the route resolves the report set once and gates once, never per row',
      metadata: routeMeta(null),
    },

    getAttentionList: {
      method: 'GET',
      path: '/attention-list',
      query: getAttentionListQuerySchema,
      responses: {
        200: z.array(attentionRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Reports below the score threshold (or the route's default) — \"Attention Required\" on the mockup; each row's suggestedAction deep-links into Section 3's createMeeting, prefilled",
      metadata: routeMeta(null),
    },

    getCycleCompletion: {
      method: 'GET',
      path: '/cycle-completion',
      query: getCycleCompletionQuerySchema,
      responses: {
        200: cycleCompletionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: '"Submitted 7/8" — reviews submitted vs. total assigned in one cycle at one scope',
      metadata: routeMeta(null),
    },

    getTalentGrid: {
      method: 'GET',
      path: '/talent-grid',
      query: getTalentGridQuerySchema,
      responses: {
        200: z.array(talentGridCellSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        '9-box grid: PerformanceReview.overallScore x potentialRating banded into a 3x3 cell count each — people list omitted below the k-anonymity floor, count always present',
      metadata: routeMeta(null),
    },

    getSkillGaps: {
      method: 'GET',
      path: '/skill-gaps',
      query: getSkillGapsQuerySchema,
      responses: {
        200: z.array(skillGapRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Per-competency gap between rated level and Competency.expectedLevelByGrade, aggregated by team, k-anonymity floored per row',
      metadata: routeMeta(null),
    },

    exportSummary: {
      method: 'POST',
      path: '/export',
      body: exportSummaryBodySchema,
      responses: {
        202: exportSummaryResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Request an org-scale CSV/Excel export (async): returns 202 {jobId} only; the worker builds the file and notifies the requester, who downloads it via getExportDownload. analytics.performance:export (never auditor). Requires Idempotency-Key',
      metadata: routeMeta('analytics.performance:export'),
    },

    getExportDownload: {
      method: 'GET',
      path: '/exports/:jobId/download',
      pathParams: exportDownloadParamsSchema,
      responses: {
        200: exportDownloadResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Mint a presigned download URL for a finished export. Requester-only (any other user gets 404); 409 while the export is not ready yet',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/performance/insights' },
);
